import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Landmark } from "lucide-react";
import PortalBankFeedStrip from "@/components/portal_bank_feed/PortalBankFeedStrip";
import { listPortalBankSyncRuns } from "@/lib/api";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function MidDayBankCheck() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRuns = useCallback(async () => {
    try {
      const r = await listPortalBankSyncRuns(10, "AUS");
      setRuns(r?.runs || []);
    } catch { /* noop */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  return (
    <div className="p-6 space-y-4 max-w-5xl" data-testid="aus-midday-bank-check-page">
      <div className="flex items-center gap-3">
        <Landmark className="w-6 h-6 text-slate-600" />
        <div>
          <h1 className="text-xl font-bold text-slate-800">Mid-Day Bank Check — Australia</h1>
          <p className="text-sm text-slate-500">
            Ebury (AUD) collections feed — today&apos;s credits pulled via Open Banking and mirrored to
            the Australia Airtable base. Replaces the Make.com scenario.
          </p>
        </div>
      </div>

      <PortalBankFeedStrip region="AUS" syncToday title="Ebury Bank Feed" />

      <Card className="border-slate-200">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold text-slate-700">Recent sync runs</CardTitle>
          <Button size="sm" variant="ghost" onClick={loadRuns} data-testid="aus-bank-runs-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <p className="text-sm text-slate-500 py-4 text-center">Loading…</p>
          ) : runs.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center" data-testid="aus-bank-runs-empty">
              No AUS syncs yet — connect Ebury above, then hit Sync now.
            </p>
          ) : (
            <table className="w-full text-sm" data-testid="aus-bank-runs-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-1.5 pr-3">When</th>
                  <th className="py-1.5 pr-3">Range</th>
                  <th className="py-1.5 pr-3">Trigger</th>
                  <th className="py-1.5 pr-3">New rows</th>
                  <th className="py-1.5 pr-3">Skipped (dupes)</th>
                  <th className="py-1.5">Errors</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.run_id} className="border-b last:border-0">
                    <td className="py-1.5 pr-3 whitespace-nowrap">{fmtDateTime(r.started_at)}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap text-slate-500">
                      {r.date_from === r.date_to ? r.date_from : `${r.date_from} → ${r.date_to}`}
                    </td>
                    <td className="py-1.5 pr-3">
                      <Badge variant="outline" className="text-xs">{r.trigger}</Badge>
                    </td>
                    <td className="py-1.5 pr-3 font-medium text-emerald-700">+{r.total_new_rows || 0}</td>
                    <td className="py-1.5 pr-3 text-slate-500">{r.total_skipped || 0}</td>
                    <td className="py-1.5">
                      {(r.errors || []).length === 0
                        ? <span className="text-xs text-emerald-600">none</span>
                        : <span className="text-xs text-red-600" title={(r.errors || []).join("\n")}>
                            {(r.errors || []).length} — hover to view
                          </span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
